import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select'
import {
  TrendingUp, Calendar, Users, ArrowUpDown
} from 'lucide-react'
import { API_BASE_URL } from '../../../lib/config'
import { formatISTDate } from '../../../components/lib/dateUtils'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions
} from 'chart.js'
import { Line } from 'react-chartjs-2'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

interface DashboardStats {
  totalUsers: number
  activeUsers: number
  totalChats: number
  totalMessages: number
  totalDeletedMessages: number
  totalSuggestions: number
  todayUsers: number
  todayChats: number
  todayMessages: number
}

interface AnalyticsData {
  userGrowth: Array<{ date: string; count: number }>
  chatActivity: Array<{ date: string; count: number }>
  messageActivity: Array<{ date: string; count: number }>
  topUsers: Array<{ userId: string; username: string; email: string; chatCount: number; messageCount: number; lastActive?: string }>
}

type SortOption = 'chatCount' | 'messageCount' | 'recent'

export function AnalyticsDashboard({ stats }: { stats: DashboardStats }) {
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    userGrowth: [],
    chatActivity: [],
    messageActivity: [],
    topUsers: []
  })
  const [loading, setLoading] = useState(false)
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d')
  const [sortBy, setSortBy] = useState<SortOption>('recent')

  const fetchAnalytics = useCallback(async () => {
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}admin/analytics?range=${timeRange}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      })
      
      if (response.ok) {
        const data = await response.json()
        console.log('Analytics data received:', data)
        console.log('Top users:', data.topUsers)
        setAnalytics(data)
      }
    } catch (error) {
      console.error('Error fetching analytics:', error)
    } finally {
      setLoading(false)
    }
  }, [timeRange])

  useEffect(() => {
    fetchAnalytics()
  }, [fetchAnalytics])

  // Sort top users based on selected criteria
  const sortedTopUsers = [...analytics.topUsers].sort((a, b) => {
    switch (sortBy) {
      case 'chatCount':
        return b.chatCount - a.chatCount
      case 'messageCount':
        return b.messageCount - a.messageCount
      case 'recent': {
        // For recent activity, use the actual last active timestamp
        if (a.lastActive && b.lastActive) {
          return new Date(b.lastActive).getTime() - new Date(a.lastActive).getTime()
        } else if (a.lastActive && !b.lastActive) {
          return -1 // a has activity, b doesn't
        } else if (!a.lastActive && b.lastActive) {
          return 1 // b has activity, a doesn't
        } else {
          // Both have no activity, fall back to chat count
          return b.chatCount - a.chatCount
        }
      }
      default:
        return 0
    }
  })

  // Prepare chart data
  const chartData = {
    labels: analytics.userGrowth.map(item => 
      new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    ),
    datasets: [
      {
        label: 'Active Users',
        data: analytics.userGrowth.map(item => item.count),
        borderColor: 'rgb(79, 70, 229)',
        backgroundColor: 'rgba(79, 70, 229, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 5,
        pointHoverRadius: 8,
        pointBackgroundColor: 'rgb(79, 70, 229)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointHoverBackgroundColor: 'rgb(79, 70, 229)',
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 3,
      },
      {
        label: 'Chats',
        data: analytics.chatActivity.map(item => item.count),
        borderColor: 'rgb(22, 163, 74)',
        backgroundColor: 'rgba(22, 163, 74, 0.1)',
        fill: true,
        tension: 0.4,
        borderDash: [5, 5],
        pointRadius: 5,
        pointHoverRadius: 8,
        pointBackgroundColor: 'rgb(22, 163, 74)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointHoverBackgroundColor: 'rgb(22, 163, 74)',
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 3,
      },
    ],
  }

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 13,
            weight: 500,
          },
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              const unit = label.includes('Active Users') ? 'users' : 'chats';
              label += context.parsed.y + ' ' + unit;
            }
            return label;
          }
        }
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: {
            size: 11,
          },
          callback: function(value) {
            return value;
          }
        },
      },
    },
  }

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex items-center gap-4">
        <Calendar className="h-5 w-5 text-gray-500" />
        <div className="flex gap-2">
          <button
            onClick={() => setTimeRange('7d')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              timeRange === '7d'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            7 Days
          </button>
          <button
            onClick={() => setTimeRange('30d')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              timeRange === '30d'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            30 Days
          </button>
          <button
            onClick={() => setTimeRange('90d')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              timeRange === '90d'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            90 Days
          </button>
        </div>
      </div>

      {/* Combined User Activity Chart with Chart.js */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-indigo-600" />
            User Activity Over Time
          </CardTitle>
          <CardDescription>Total users and active users trend</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-96 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
            </div>
          ) : analytics.userGrowth.length > 0 ? (
            <div className="h-96">
              <Line data={chartData} options={chartOptions} />
            </div>
          ) : (
            <div className="h-96 flex items-center justify-center text-gray-500">
              No data available
            </div>
          )}
        </CardContent>
      </Card>

                {/* Top Users */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Users className="h-5 w-5 text-purple-600" />
                          Most Active Users
                          {sortBy === 'recent' && (
                            <span className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-2 py-1 rounded-full">
                              Recent Activity
                            </span>
                          )}
                        </CardTitle>
                        <CardDescription>
                          {sortBy === 'recent' 
                            ? 'Users sorted by most recent chat activity (based on last message timestamp)'
                            : 'Users with the most chat activity'
                          }
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <ArrowUpDown className="h-4 w-4 text-gray-500" />
                        <Select value={sortBy} onValueChange={(value: SortOption) => setSortBy(value)}>
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Sort by" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="chatCount">Chat Count</SelectItem>
                            <SelectItem value="messageCount">Message Count</SelectItem>
                            <SelectItem value="recent">Recent Activity</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-48 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
          ) : sortedTopUsers.length > 0 ? (
            <div className="space-y-4">
              {sortedTopUsers.map((user, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {user.username}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {user.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4 items-center">
                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Last Active</p>
                      {user.lastActive ? (
                        <div className="text-xs font-medium text-gray-900 dark:text-white">
                          {formatISTDate(user.lastActive)}
                        </div>
                      ) : (
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Never</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Chats</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">
                        {user.chatCount}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Messages</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">
                        {user.messageCount}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-500">
              No user activity data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Average Messages per Chat</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-indigo-600">
              {stats.totalChats > 0 ? (stats.totalMessages / stats.totalChats).toFixed(1) : '0'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">User Engagement Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {stats.totalUsers > 0 ? ((stats.activeUsers / stats.totalUsers) * 100).toFixed(1) : '0'}%
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Regenerated Messages</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">
              {stats.totalDeletedMessages}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

